import {decryptData} from '../../utils/crypto';

/**
 * Read and parse one of Canvas's encrypted localStorage lists.
 *
 * @param {string} key storage key
 * @return {Array} the list, or empty when absent or unreadable
 */
function storedList(key) {
	try {
		const stored = localStorage.getItem(key);
		if (!stored) return [];
		const decoded = decryptData(stored);
		if (!decoded) return [];
		const parsed = JSON.parse(decoded);
		return Array.isArray(parsed) ? parsed : [];
	} catch (_) {
		return [];
	}
}

/**
 * The Canvas permissions this user holds, as the agent backend needs them.
 *
 * Built from the two lists Canvas already stores, which is why the agent
 * asks nobody for them - no extra request, no change to login.
 *
 * `permission` is the grant: bare feature names (`apssid`, `packetcapture`),
 * no actions, the list `fetchPermission(name)` checks.
 *
 * `rolepermission` is the catalogue: one row per feature and action, with
 * the `codename` (`view_apssid`) and the product-line flags that
 * `fetchPermission(name, action)` reads. Its rule is reproduced here - the
 * `wifi` flag decides, except for `switch` and `license`, which the UI
 * exempts - so the chat offers what the screens offer.
 *
 * The two are combined rather than chosen between. A feature must be
 * granted, and then the catalogue decides which of its actions exist. The
 * result is per-action (`apssid.delete`), which is how the agent's policies
 * are written, so they match exactly instead of by feature alone.
 *
 * A granted feature the catalogue says nothing about keeps its plain name.
 * That case is real and common: 44 of one account's 141 granted features
 * have no catalogue row at all, `reports` among them, and the agent's
 * policies need `reports.view`. Treating a missing row as a denial would
 * have switched those off for everyone.
 *
 * None of this is proof of anything - the storage behind it is editable, and
 * the backend treats the result as what to offer rather than what to allow.
 * Canvas checks again when the browser fetches the data itself.
 *
 * @return {string[]} `feature.action` entries, plus bare features where the
 *   catalogue has no opinion
 */
export function canvasPermissions() {
	const granted = storedList('permission')
		.filter((entry) => typeof entry === 'string' && entry.trim())
		.map((entry) => entry.trim());
	if (!granted.length) return [];

	const rowsByFeature = new Map();
	for (const row of storedList('rolepermission')) {
		if (!row || typeof row.feature !== 'string') continue;
		if (!rowsByFeature.has(row.feature)) rowsByFeature.set(row.feature, []);
		rowsByFeature.get(row.feature).push(row);
	}

	const held = new Set();
	for (const feature of granted) {
		const rows = rowsByFeature.get(feature);
		if (!rows || !rows.length) {
			// The catalogue has no row for it, so there is nothing to narrow
			// by. The grant stands as given.
			held.add(feature);
			continue;
		}
		const actions = [];
		let listed = 0;
		for (const row of rows) {
			if (typeof row.action !== 'string' || !row.action) continue;
			listed += 1;
			// `fetchPermission`'s own exemption: these two are not gated on
			// the wireless product line.
			const exempt = row.feature === 'switch' || row.feature === 'license';
			if (exempt || row.wifi) actions.push(row.action);
		}
		if (!actions.length) continue;
		if (actions.length === listed) {
			// Every action the catalogue lists is allowed, so naming them
			// one by one says nothing the feature does not. Sent bare, which
			// the backend reads as covering each of them. This is not a
			// detail: spelled out, one real account's list came to 5980
			// bytes of header, and a proxy's default buffer is 8192.
			held.add(feature);
			continue;
		}
		for (const action of actions) held.add(`${feature}.${action}`);
	}
	return [...held];
}

/**
 * The header value, or null when there is nothing worth sending.
 *
 * Null rather than an empty string on purpose: an empty header would say
 * "this user holds nothing", which refuses everything. Sending no header at
 * all says "no claim made", which is what a browser whose storage has not
 * loaded yet should say.
 *
 * @return {string|null} comma-separated permissions
 */
export function canvasPermissionHeader() {
	const held = canvasPermissions();
	if (!held.length) return null;
	return held.join(',');
}
